import { useState } from "react";
import { toast } from "sonner";
import { Images, UploadSimple, Trash } from "@phosphor-icons/react";
import { api, API_ROOT, errMsg } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";

export default function MoodBoard() {
  const { data, loading, refetch } = useGet("/client/moodboard");
  const [uploading, setUploading] = useState(false);
  const items = data || [];

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/client/moodboard", fd); toast.success("Image added to your mood board."); refetch(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/client/moodboard/${id}`); toast.success("Image removed."); refetch(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <Loading label="Loading mood board" />;

  return (
    <div>
      <PageHeader eyebrow="Inspiration" title="Mood Board" description="Collect the looks, colours and details that inspire your celebration.">
        <label className="inline-flex">
          <input type="file" accept="image/*" hidden onChange={onUpload} data-testid="moodboard-file-input" />
          <span className={`inline-flex items-center rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 text-sm cursor-pointer transition-colors duration-200 ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="upload-image-btn">
            <UploadSimple size={18} className="mr-1" /> {uploading ? "Uploading…" : "Add image"}
          </span>
        </label>
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState icon={Images} title="Your mood board is empty" description="Upload images to start building your inspiration board." />
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {items.map((m) => (
            <div key={m.id} data-testid={`moodboard-item-${m.id}`} className="group relative mb-5 break-inside-avoid overflow-hidden rounded-2xl border border-border">
              <img src={`${API_ROOT}/client/moodboard/file/${m.id}`} alt={m.original_filename || "Mood board image"} className="w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/30 transition-colors duration-200" />
              <button onClick={() => remove(m.id)} data-testid={`delete-moodboard-${m.id}`} className="absolute top-3 right-3 rounded-full bg-white/90 p-2 text-destructive opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-white">
                <Trash size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
